import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Diamond, SquaresFour, Package, ImagesSquare, CreditCard, Gear, SignOut, Receipt, PlugsConnected, ShieldStar, ChartLineUp, Storefront, UsersThree, Palette, CaretLeft, CaretRight, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTheme } from "@/context/ThemeContext";
import { Moon, Sun } from "@phosphor-icons/react";

const nav = [
  { to: "/dashboard", icon: SquaresFour, key: "overview", end: true },
  { to: "/dashboard/products", icon: Package, key: "products" },
  { to: "/dashboard/logos", icon: ImagesSquare, key: "logos" },
  { to: "/dashboard/store", icon: Storefront, key: "store" },
  { to: "/dashboard/design", icon: Palette, key: "design" },
  { to: "/dashboard/orders", icon: Receipt, key: "orders" },
  { to: "/dashboard/promotions", icon: Tag, key: "promotions" },
  { to: "/dashboard/team", icon: UsersThree, key: "team" },
  { to: "/dashboard/analytics", icon: ChartLineUp, key: "analytics" },
  { to: "/dashboard/integrations", icon: PlugsConnected, key: "integrations" },
  { to: "/dashboard/billing", icon: CreditCard, key: "billing" },
  { to: "/dashboard/settings", icon: Gear, key: "settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("hephy_sidebar_collapsed") === "1");
  const [hidden, setHidden] = useState(false);
  useEffect(() => { localStorage.setItem("hephy_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    const onFs = (e) => setHidden(!!e.detail);
    window.addEventListener("hephy-fullscreen", onFs);
    return () => { window.removeEventListener("hephy-fullscreen", onFs); setHidden(false); };
  }, []);
  const doLogout = async () => { await logout(); toast.success(t("nav.loggedOut")); navigate(catalog ? "/equipo" : "/login"); };
  const backToTeam = async () => { await logout(); navigate("/equipo"); };
  const delegated = !!user?.delegated_admin;
  const catalog = user?.scope === "catalog";
  let items = [...nav].filter((n) => !(delegated && n.key === "billing"));
  if (catalog) items = nav.filter((n) => n.key === "products" || n.key === "logos");
  if (!catalog && user?.role === "admin") items.push({ to: "/dashboard/admin", icon: ShieldStar, key: "adminPlatform" });

  // Catalog-scoped members may only use Productos and Logos; send them there from anywhere else.
  useEffect(() => {
    if (!catalog) return;
    const p = window.location.pathname;
    const ok = p.startsWith("/dashboard/products") || p.startsWith("/dashboard/logos");
    if (!ok) navigate("/dashboard/products", { replace: true });
  }, [catalog, navigate]);

  const asideW = collapsed ? "w-16" : "w-64";
  const mainM = hidden ? "ml-0" : (collapsed ? "ml-16" : "ml-64");

  return (
    <div className="min-h-screen bg-obsidian text-neutral-200 flex">
      <aside className={`${asideW} ${hidden ? "hidden" : "flex"} shrink-0 border-r border-white/10 flex-col fixed h-screen bg-[#0d0d0d] z-40 transition-all duration-200`} data-testid="dash-sidebar">
        <div className={`h-16 flex items-center ${collapsed ? "justify-center px-0" : "gap-2 px-6"} border-b border-white/10 relative`}>
          <img src="/hephy-logo.png" alt="Hephy 3D" className="w-8 h-8 object-contain" />
          {!collapsed && <span className="font-serif text-lg">Hephy 3D</span>}
          <button onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-toggle" title={collapsed ? "Expandir menú" : "Colapsar menú"}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#1a1a1a] border border-white/15 flex items-center justify-center text-neutral-300 hover:text-white hover:bg-white/10 z-50">
            {collapsed ? <CaretRight size={13} weight="bold" /> : <CaretLeft size={13} weight="bold" />}
          </button>
        </div>
        {delegated && !catalog && !collapsed && (
          <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-gold/10 border border-gold/25 text-[11px] text-gold flex items-center gap-2" data-testid="delegated-admin-banner">
            <ShieldStar size={15} weight="fill" />
            <span>Sesión de administrador{user?.actor_name ? ` · ${user.actor_name}` : ""}</span>
          </div>
        )}
        {catalog && !collapsed && (
          <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-[11px] text-emerald-300 flex flex-col gap-2" data-testid="catalog-mode-banner">
            <div className="flex items-center gap-2"><Package size={15} weight="fill" />
              <span>Modo gestión de productos{user?.actor_name ? ` · ${user.actor_name}` : ""}</span></div>
            <button onClick={backToTeam} data-testid="catalog-back-to-team" className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-[11px] font-semibold text-obsidian hover:bg-gold/90 transition-colors">
              <CaretLeft size={13} weight="bold" /> Volver al equipo
            </button>
          </div>
        )}
        {catalog && collapsed && (
          <button onClick={backToTeam} data-testid="catalog-back-to-team-collapsed" title="Volver al equipo" className="mx-auto mt-3 w-9 h-9 rounded-full bg-gold flex items-center justify-center text-obsidian hover:bg-gold/90">
            <CaretLeft size={16} weight="bold" />
          </button>
        )}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${t("nav." + n.key)}`} title={collapsed ? t("nav." + n.key) : undefined}
              className={({ isActive }) => `flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={20} weight="duotone" /> {!collapsed && t("nav." + n.key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          {!collapsed && (
            <div className="px-2 pb-2 flex items-center justify-between gap-2">
              <LanguageSwitcher />
              <button onClick={toggle} title={theme === "light" ? "Modo oscuro" : "Modo claro"} data-testid="theme-toggle" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-xs text-neutral-300 hover:bg-white/5">
                {theme === "light" ? <Moon size={15} weight="fill" /> : <Sun size={15} weight="fill" />}
                {theme === "light" ? "Oscuro" : "Claro"}
              </button>
            </div>
          )}
          {collapsed && (
            <button onClick={toggle} title={theme === "light" ? "Modo oscuro" : "Modo claro"} data-testid="theme-toggle" className="w-full flex items-center justify-center py-2 rounded-lg text-neutral-300 hover:bg-white/5">
              {theme === "light" ? <Moon size={17} weight="fill" /> : <Sun size={17} weight="fill" />}
            </button>
          )}
          {!collapsed && <div className="px-3 py-2 text-xs text-neutral-500 truncate">{user?.email}</div>}
          <button onClick={doLogout} data-testid="logout-btn" title={collapsed ? t("nav.logout") : undefined} className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm text-neutral-400 hover:text-white hover:bg-white/5`}>
            <SignOut size={20} /> {!collapsed && t("nav.logout")}
          </button>
        </div>
      </aside>
      {hidden && (
        <button onClick={() => window.dispatchEvent(new CustomEvent("hephy-fullscreen", { detail: false }))} data-testid="exit-fullscreen-floating"
          className="fixed top-3 left-3 z-50 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-[#1a1a1a] border border-white/15 text-neutral-200 hover:bg-white/10">
          <CaretRight size={13} weight="bold" /> Mostrar menú
        </button>
      )}
      <main className={`flex-1 ${mainM} min-h-screen transition-all duration-200`}>
        <Outlet />
      </main>
    </div>
  );
}
